import React, { Fragment, useCallback, useEffect, useMemo } from 'react';
import { Matrix4, Vector3 } from 'three';

import { EnvelopeEndConnectionType, JackGirderConnection, TrussEnvelope } from '@paragon/contract-library';
import { Direction2D, Line2D, Point2D, Polygon2D } from '@paragon/geometry-library';
import { calculateTrussPolygon2D, TrussSolidCalculationDependencies } from '@paragon/layout';
import { RaycastOrder, RenderOrder } from '@paragon/three-shared';
import { getModifierKeys } from '@paragon/utilities';

import { useAppDispatch, useAppSelector } from '../../../components/hooks';
import { getTrussSolidCalculationDependencies } from '../../../library/layout-geometry/truss-envelopes/trussSolidSelectors';
import { selectJackComponentEnvelopesOfComponentDesign } from '../../../store/actions/selectActions';
import { setHoveredJackComponentGuidIfApplicable } from '../../../store/actions/trussGroups';
import { getJackGirderConnectionsByGirderGuid } from '../../../store/selectors/getJackGirderConnections';
import { getAllTrussEnvelopePositions } from '../../../store/selectors/layoutDocumentSelectors';
import { is3DLayoutCameraInUse } from '../../../store/selectors/layoutModeSelectors';
import { getActiveConnectionMode } from '../../../store/selectors/layoutVisualOptionsSelectors';
import { threeJsDom } from '../../util/events/ThreeJSDom';
import { LabelVisual } from '../../util/LabelVisual';
import { layoutColors } from '../layoutColors';

type ChordType = Exclude<EnvelopeEndConnectionType, 'None'>;

const VISUAL_OPACITY = 0.5;
const VISUAL_OVERHANG = 0.5;

const PRIORITIZED_ELEVATION_OFFSET = 1 / 16;

const CHORD_TYPE_GLYPHS = {
  TopChord: 'TC',
  BottomChord: 'BC',
  TopAndBottomChords: '//',
} as const satisfies Record<ChordType, string>;

export function TrussEnvelopeJackGirderConnectionVisualsController(props: {
  envelopes: TrussEnvelope[];
  selectedEnvelopeGuids: ReadonlySet<string>;
}) {
  const in3D = useAppSelector(is3DLayoutCameraInUse);
  const renderMode = useAppSelector(getActiveConnectionMode);

  const envelopePositions = useAppSelector(getAllTrussEnvelopePositions);
  const connectionsByGirderGuid = useAppSelector(getJackGirderConnectionsByGirderGuid);
  const calculationDependencies = useAppSelector(getTrussSolidCalculationDependencies);

  const visuals = useMemo(() => {
    const selectedEnvelopeComponentDesignGuids = new Set<string>();
    for (const envelope of props.envelopes) {
      if (envelope.componentDesignGuid != null && props.selectedEnvelopeGuids.has(envelope.guid)) {
        selectedEnvelopeComponentDesignGuids.add(envelope.componentDesignGuid);
      }
    }

    const standard: ConnectionVisualProps[] = [];
    const prioritized: ConnectionVisualProps[] = [];

    for (const envelope of props.envelopes) {
      if (envelope.componentDesignGuid == null) continue;
      const connections = connectionsByGirderGuid[envelope.componentDesignGuid];
      if (connections == null || connections.length === 0) continue;

      const matrix = envelopePositions[envelope.guid];
      if (matrix == null) continue;

      const isSelectedGirderEnvelope = props.selectedEnvelopeGuids.has(envelope.guid);

      for (const visual of calculateConnectionVisuals(envelope, connections, matrix, in3D, calculationDependencies)) {
        if (isSelectedGirderEnvelope || selectedEnvelopeComponentDesignGuids.has(visual.jackGuid)) {
          visual.position.y += PRIORITIZED_ELEVATION_OFFSET;
          prioritized.push(visual);
        } else {
          standard.push(visual);
        }
      }
    }

    return [...standard, ...prioritized];
  }, [
    props.envelopes,
    props.selectedEnvelopeGuids,
    connectionsByGirderGuid,
    envelopePositions,
    in3D,
    calculationDependencies,
  ]);

  if (renderMode !== 'connections' && renderMode !== 'hardwareAndConnections') return null;

  return <BulkJackGirderConnectionVisuals visuals={visuals} in3D={in3D} />;
}

interface ConnectionVisualProps {
  key: string;
  jackGuid: string;
  position: Vector3;
  radius: number;
  chordType: ChordType;
}

function calculateConnectionVisuals(
  envelope: TrussEnvelope,
  connections: JackGirderConnection[],
  matrix: Matrix4,
  in3D: boolean,
  trussSolidDependencies: TrussSolidCalculationDependencies,
): ConnectionVisualProps[] {
  const connectionGroups = groupConnectionsByJackAndLocation(connections, { mergeTopAndBottomChords: !in3D });

  const trussEnvelopeThickness = envelope.thickness * envelope.numberOfPlies;
  const radius = trussEnvelopeThickness * (0.5 + VISUAL_OVERHANG);

  const trussPolygon = in3D ? calculateTrussPolygon2D(envelope, trussSolidDependencies) : null;

  const result: ConnectionVisualProps[] = [];

  for (const [groupKey, group] of connectionGroups) {
    const chordType = classifyChordTypeOfConnectionGroup(group);
    const elevation = trussPolygon ? getTrussSolidElevationAt(trussPolygon, group.xLocation, chordType) : 0;
    const worldPosition = new Vector3(group.xLocation, elevation, trussEnvelopeThickness / 2).applyMatrix4(matrix);

    result.push({
      key: `${envelope.guid}|${groupKey}`,
      jackGuid: group.jackGuid,
      position: worldPosition,
      radius,
      chordType,
    });
  }

  return result;
}

function getTrussSolidElevationAt(trussPolygon: Polygon2D, xLocation: number, chordType: ChordType): number {
  const verticalLine = new Line2D(new Point2D(xLocation, 0), Direction2D.up);
  const elevations = trussPolygon
    .findSegmentIntersectionsWithLine(verticalLine)
    .flatMap((segment) => segment.endPoints.map((point) => point.y));

  if (elevations.length === 0) return 0;

  return chordType === 'TopChord' ? Math.max(...elevations) : Math.min(...elevations);
}

function BulkJackGirderConnectionVisuals({ visuals, in3D }: { visuals: ConnectionVisualProps[]; in3D: boolean }) {
  const dispatch = useAppDispatch();

  const clearHoveredJackTruss = useCallback(() => {
    dispatch(setHoveredJackComponentGuidIfApplicable(null));
  }, [dispatch]);

  useEffect(() => clearHoveredJackTruss, [clearHoveredJackTruss]);

  return (
    <group>
      {visuals.map((visual) => {
        const position = [visual.position.x, visual.position.y, visual.position.z] as const;
        const color = layoutColors.jackGirderConnections[visual.chordType];
        const glyph = CHORD_TYPE_GLYPHS[visual.chordType];

        return (
          <Fragment key={visual.key}>
            <mesh
              position={position}
              renderOrder={in3D ? RenderOrder.LabelVisual : 0}
              userData-raycastOrder={RaycastOrder.JackGirderConnectionContainer}
              onClick={(event) => {
                if (!threeJsDom.shouldDispatchClickEvent()) return;

                event.stopPropagation();

                dispatch(
                  selectJackComponentEnvelopesOfComponentDesign(visual.jackGuid, {
                    onlyIfVisible: true,
                    intent: 'toggle',
                    exclusive: !getModifierKeys(event).ctrlKey,
                  }),
                );
              }}
              onPointerMove={(event) => {
                event.stopPropagation();
                dispatch(setHoveredJackComponentGuidIfApplicable(visual.jackGuid));
              }}
              onPointerOut={clearHoveredJackTruss}
            >
              <meshBasicMaterial
                color={color}
                transparent
                opacity={VISUAL_OPACITY}
                depthTest={in3D}
                depthWrite={false}
              />
              <sphereGeometry args={[visual.radius]} />
            </mesh>

            <LabelVisual
              position={position}
              renderOrder={0}
              fontSize={visual.radius * 1.1}
              color="black"
              outlineColor={0xffffff}
            >
              {glyph}
            </LabelVisual>
          </Fragment>
        );
      })}
    </group>
  );
}

interface ConnectionGroup {
  jackGuid: string;
  xLocation: number;

  hasTopChord: boolean;
  hasBottomChord: boolean;
}

function classifyChordTypeOfConnectionGroup(group: ConnectionGroup): ChordType {
  if (group.hasTopChord && group.hasBottomChord) return 'TopAndBottomChords';
  if (group.hasTopChord) return 'TopChord';
  return 'BottomChord';
}

function expandToOneConnectionPerJack(connections: JackGirderConnection[]): JackGirderConnection[] {
  return connections.flatMap((connection) =>
    Array.from({ length: connection.numberOfJacks }, (_, jackIndex) => ({
      ...connection,
      xLocation: connection.xLocation + connection.spacingOfJacks * jackIndex,
    })),
  );
}

function groupConnectionsByJackAndLocation(
  connections: JackGirderConnection[],
  options: { mergeTopAndBottomChords: boolean },
): Map<string, ConnectionGroup> {
  const groups = new Map<string, ConnectionGroup>();
  for (const connection of expandToOneConnectionPerJack(connections)) {
    const locationKey = `${connection.jackGuid}|${Math.round(connection.xLocation * 16)}`;
    const key = options.mergeTopAndBottomChords ? locationKey : `${locationKey}|${connection.memberType}`;

    const existingConnection = groups.get(key);

    if (existingConnection) {
      existingConnection.hasTopChord ||= connection.memberType === 'TopChord';
      existingConnection.hasBottomChord ||= connection.memberType === 'BottomChord';
    } else {
      groups.set(key, {
        jackGuid: connection.jackGuid,
        xLocation: connection.xLocation,
        hasTopChord: connection.memberType === 'TopChord',
        hasBottomChord: connection.memberType === 'BottomChord',
      });
    }
  }
  return groups;
}
